import type { HTMLAttributes } from "react";
import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../shared/core/i18n/use-intl";
import { StaticAssets } from "../styles/custom-assets";
import { theme } from "../styles/theme";
import { UIConstants } from "../styles/uiConstants";

/**
 * The blue half of the signed-out screens: logo, pitch, and whatever the screen wants below.
 * Shared by the login screen and the enrollment layout so both stay in step.
 */
export function BrandPanel(props: Readonly<HTMLAttributes<HTMLElement>>) {
  const { children, ...rest } = props;
  const { formatMessage } = useIntl();

  return (
    <Panel {...rest}>
      <Logo src={StaticAssets.brandLogo} alt="" />
      <Pitch>
        <Headline>{formatMessage("authScreen.headline")}</Headline>
        <Description>{formatMessage("authScreen.headlineDescription")}</Description>
      </Pitch>
      {children}
    </Panel>
  );
}

const Panel = styled.section`
  display: flex;
  flex: 0 0 42%;
  flex-direction: column;
  padding: 64px;
  background: ${theme.brandGradient};
  color: #ffffff;

  @media (max-width: ${UIConstants.TABLET_BREAKPOINT}px) {
    flex: 0 0 auto;
    padding: 32px;
  }
`;

const Logo = styled.img`
  width: 186px;
  object-fit: contain;
  align-self: flex-start;
`;

/**
 * Sits in the lower half of the panel: centred in the space the logo leaves below it, then
 * pushed down by half the top padding, which puts its centre at the mockup's 60% mark.
 */
const Pitch = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  max-width: 560px;
  padding-top: 16vh;
`;

const Headline = styled.h1`
  ${theme.boldText};
  margin: 0 0 32px;
  font-size: 52px;
  line-height: 68px;
  color: #ffffff;
`;

const Description = styled.p`
  ${theme.text};
  margin: 0;
  font-size: 22px;
  line-height: 33px;

  color: ${theme.colors.gray[100]};
  opacity: 0.8;
`;
